import { defineComponent as _defineComponent } from 'vue'
import { computed, onUnmounted, ref, watch } from 'vue'
import { api } from "@/main";
import { Subscription } from "@/api";
import Card from "@/components/Card.vue";
import router from "@/router";
import EditableObject from "@/components/EditableObject.vue";
import Permissions from "@/components/Permissions.vue";

interface Habit {
  id: string;
  version?: number;
  habitHolderId?: string;
  name: string;
  frequency: string;
  recurrenceRule?: string;
  reminderLocalTime?: string;
  lastReminderWindowEndDate?: string;
  createdByUserId?: string;
  createdTimestamp?: string;
  deletedByUserId?: string;
  deletedTimestamp?: string;
}

interface HabitGoal {
  id?: string;
  version?: number;
  habitId: string;
  comparison: string;
  goalValue: number;
  startDate?: string;
  endDate?: string;
  createdTimestamp?: string;
  deletedTimestamp?: string;
}

interface HabitValue {
  habitId: string;
  localDate: string;
  value: number;
}

interface UIHabitHolder {
  id: string;
  habitHolder: {
    id: string;
    version?: number;
    householdId?: string;
    name: string;
    createdByUserId?: string;
    createdTimestamp?: string;
    deletedByUserId?: string;
    deletedTimestamp?: string;
  };
  habits: Habit[];
  goals: HabitGoal[];
  values: HabitValue[];
}

interface HabitCalendarEvent {
  name: string;
  start: string;
  end: string;
  color: string;
  timed: boolean;
  habit: Habit;
  localDate: string;
  value: number;
  met: boolean;
}

interface HabitMobileEvent {
  habit: Habit;
  localDate: string;
  name: string;
  value: number;
  color: string;
  future: boolean;
  canEdit: boolean;
}

interface HabitMobileDay {
  label: string;
  localDate: string;
  events: HabitMobileEvent[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'HabitHolder',
  props: ['household', 'habitHolderId', 'habitId'],
  emits: ['breadcrumbs', 'menuItems'],
  setup(__props, { emit }) {

const props = __props




const habitHolder = ref<UIHabitHolder | null>(null);
let subscription: Subscription | null = null;

const formatLocalDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const today = formatLocalDate(new Date());
const savingHabit = ref(false);
const showStartDatePickerMenu = ref(false);
const showReminderTimePickerMenu = ref(false);
const savingEditHabit = ref(false);
const showEditStartDatePickerMenu = ref(false);
const showEditEndDatePickerMenu = ref(false);
const showEditReminderTimePickerMenu = ref(false);
const calendarFocus = ref(today.substring(0, 7) + '-01');
const frequencyOptions = [
  { text: 'Daily', value: 'DAILY' },
  { text: 'Weekly', value: 'WEEKLY' },
  { text: 'Monthly', value: 'MONTHLY' },
  { text: 'Yearly', value: 'YEARLY' },
];
const rules = {
  required: (value: string) => !!value || 'Required',
};
const comparisonOptions = [
  { text: 'Less than', value: 'LESS_THAN' },
  { text: 'At most', value: 'AT_MOST' },
  { text: 'Exactly', value: 'EXACTLY' },
  { text: 'At least', value: 'AT_LEAST' },
  { text: 'Greater than', value: 'GREATER_THAN' },
];
const weekdayOptions = [
  { text: 'M', value: 'MO' },
  { text: 'T', value: 'TU' },
  { text: 'W', value: 'WE' },
  { text: 'T', value: 'TH' },
  { text: 'F', value: 'FR' },
  { text: 'S', value: 'SA' },
  { text: 'S', value: 'SU' },
];
const newHabit = ref({
  name: '',
  frequency: 'DAILY',
  reminderLocalTime: '',
  comparison: 'AT_LEAST',
  goalValue: 1,
  startDate: today,
  endDate: '',
});
const selectedWeekdays = ref(weekdayOptions.map(day => day.value));
const editHabit = ref({
  name: '',
  frequency: 'DAILY',
  reminderLocalTime: '',
  comparison: 'AT_LEAST',
  goalValue: 1,
  startDate: today,
  endDate: '',
});
const editSelectedWeekdays = ref(weekdayOptions.map(day => day.value));

const holderPath = computed(() => '/h/' + props.household.id + '/habit/' + props.habitHolderId);
const selectedHabit = computed(() => habitHolder.value?.habits.find(habit => habit.id === props.habitId) || null);

const updateMenu = () => {
  if (!habitHolder.value)
    return;

  const menuItems = [{
    title: habitHolder.value.habitHolder.name,
    icon: 'mdi-calendar-check',
    to: holderPath.value,
    color: '#2E7D32',
  }];

  habitHolder.value.habits.forEach(habit => {
    menuItems.push({
      title: habit.name,
      icon: 'mdi-calendar-check-outline',
      to: holderPath.value + '/' + habit.id,
    });
  });

  menuItems.push({
    title: 'Create Habit',
    icon: 'mdi-plus',
    to: holderPath.value + '/create',
  });

  menuItems.push({
    title: 'Settings',
    icon: 'mdi-cog',
    to: holderPath.value + '/settings',
  });

  emit('menuItems', menuItems);
}

const updateBreadcrumbs = () => {
  if (!habitHolder.value)
    return;

  const breadcrumbs = [{
    text: habitHolder.value.habitHolder.name,
    icon: 'mdi-calendar-check',
    to: holderPath.value,
  }];

  if (props.habitId === 'create')
    breadcrumbs.push({ text: 'Create Habit', icon: 'mdi-plus', to: holderPath.value + '/create' });
  else if (props.habitId === 'settings')
    breadcrumbs.push({ text: 'Settings', icon: 'mdi-cog', to: holderPath.value + '/settings' });
  else if (selectedHabit.value)
    breadcrumbs.push({ text: selectedHabit.value.name, icon: 'mdi-calendar-check-outline', to: holderPath.value + '/' + selectedHabit.value.id });

  emit('breadcrumbs', breadcrumbs);
}

watch(() => props.habitHolderId, (newId) => {
  if (subscription)
    api.unsubscribe(subscription);

  habitHolder.value = null;
  subscription = api.subscribe('UIHabitHolder', newId, (newValue) => {
    habitHolder.value = newValue;
    if (!newValue)
      return;

    updateMenu();
    updateBreadcrumbs();
  });
}, { immediate: true });

watch(() => props.habitId, () => {
  updateBreadcrumbs();
});

onUnmounted(() => {
  if (subscription)
    api.unsubscribe(subscription);
});

const goalsByHabitId = computed(() => {
  const result: Record<string, HabitGoal[]> = {};
  (habitHolder.value?.goals || []).forEach(goal => {
    if (!result[goal.habitId])
      result[goal.habitId] = [];
    result[goal.habitId].push(goal);
  });
  return result;
});

const valuesByHabitId = computed(() => {
  const result: Record<string, HabitValue[]> = {};
  (habitHolder.value?.values || []).forEach(value => {
    if (!result[value.habitId])
      result[value.habitId] = [];
    result[value.habitId].push(value);
  });
  return result;
});

const goalForDate = (habit: Habit, localDate: string) => {
  return (goalsByHabitId.value[habit.id] || [])
    .filter(goal => (!goal.startDate || goal.startDate <= localDate) && (!goal.endDate || goal.endDate >= localDate))
    .sort((left, right) => (right.startDate || '').localeCompare(left.startDate || ''))[0] || null;
}

const currentGoal = (habit: Habit) => {
  return goalForDate(habit, today) || goalsByHabitId.value[habit.id]?.[0] || null;
}

const valueForDate = (habit: Habit, localDate: string) => {
  return valuesByHabitId.value[habit.id]?.find(value => value.localDate === localDate)?.value || 0;
}

const increment = (habit: Habit, amount: number, localDate = today) => {
  api.rpc('habit', 'incrementHabitValue', {
    habitHolderId: props.habitHolderId,
    habitId: habit.id,
    localDate,
    amount
  });
}

const parseDate = (localDate: string) => {
  const parts = localDate.split('-').map(part => Number(part));
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

const formatDate = (date: Date) => {
  return formatLocalDate(date);
}

const addDays = (localDate: string, days: number) => {
  const date = parseDate(localDate);
  date.setDate(date.getDate() + days);
  return formatDate(date);
}

const weekdayCode = (localDate: string) => {
  return ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][parseDate(localDate).getDay()];
}

const selectedDaysForHabit = (habit: Habit) => {
  const byDay = (habit.recurrenceRule || '').split(';').find(part => part.startsWith('BYDAY='));
  if (!byDay)
    return weekdayOptions.map(day => day.value);
  return byDay.substring('BYDAY='.length).split(',').filter(Boolean);
}

const isScheduledDate = (habit: Habit, localDate: string) => {
  if (habit.frequency !== 'DAILY')
    return true;
  return selectedDaysForHabit(habit).includes(weekdayCode(localDate));
}

const currentWindowDates = (habit: Habit, referenceDate: string) => {
  if (!isScheduledDate(habit, referenceDate))
    return [];

  if (habit.frequency === 'DAILY')
    return [referenceDate];

  const date = parseDate(referenceDate);
  let start: Date;
  let end: Date;
  if (habit.frequency === 'WEEKLY') {
    const day = date.getDay();
    start = new Date(date);
    start.setDate(date.getDate() - day);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (habit.frequency === 'MONTHLY') {
    start = new Date(date.getFullYear(), date.getMonth(), 1);
    end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  } else {
    start = new Date(date.getFullYear(), 0, 1);
    end = new Date(date.getFullYear(), 11, 31);
  }

  const dates: string[] = [];
  const finalDate = formatDate(end);
  for (let cursor = formatDate(start); cursor <= finalDate; cursor = addDays(cursor, 1))
    dates.push(cursor);
  return dates;
}

const windowValue = (habit: Habit, referenceDate: string) => {
  return currentWindowDates(habit, referenceDate).reduce((sum, localDate) => sum + valueForDate(habit, localDate), 0);
}

const isGoalMet = (goal: HabitGoal | null, value: number) => {
  if (!goal)
    return false;
  switch (goal.comparison) {
    case 'LESS_THAN': return value < goal.goalValue;
    case 'AT_MOST': return value <= goal.goalValue;
    case 'EXACTLY': return value === goal.goalValue;
    case 'AT_LEAST': return value >= goal.goalValue;
    case 'GREATER_THAN': return value > goal.goalValue;
    default: return false;
  }
}

const colorForGoal = (goal: HabitGoal | null, value: number) => {
  if (!goal)
    return '#546E7A';
  return isGoalMet(goal, value) ? '#2E7D32' : '#B71C1C';
}

const resetNewHabit = () => {
  newHabit.value = {
    name: '',
    frequency: 'DAILY',
    reminderLocalTime: '',
    comparison: 'AT_LEAST',
    goalValue: 1,
    startDate: today,
    endDate: '',
  };
  selectedWeekdays.value = weekdayOptions.map(day => day.value);
}

const recurrenceRuleFor = (frequency: string, selectedDays: string[]) => {
  if (frequency !== 'DAILY')
    return null;

  if (selectedDays.length === 0 || selectedDays.length === weekdayOptions.length)
    return null;

  const orderedDays = weekdayOptions
    .map(day => day.value)
    .filter(day => selectedDays.includes(day));
  return 'BYDAY=' + orderedDays.join(',');
}

const buildRecurrenceRule = () => recurrenceRuleFor(newHabit.value.frequency, selectedWeekdays.value);

const resetEditHabit = () => {
  const habit = selectedHabit.value;
  if (!habit)
    return;

  const goal = currentGoal(habit);
  editHabit.value = {
    name: habit.name,
    frequency: habit.frequency,
    reminderLocalTime: habit.reminderLocalTime || '',
    comparison: goal?.comparison || 'AT_LEAST',
    goalValue: goal?.goalValue || 1,
    startDate: goal?.startDate || today,
    endDate: goal?.endDate || '',
  };
  editSelectedWeekdays.value = selectedDaysForHabit(habit);
}

const calendarEventsFor = (habits: Habit[]) => {
  const start = addDays(calendarFocus.value, -14);
  const end = addDays(calendarFocus.value, 75);
  const events: HabitCalendarEvent[] = [];
  const seenWindows = new Set<string>();

  habits.forEach(habit => {
    for (let day = start; day <= end; day = addDays(day, 1)) {
      const windowDates = currentWindowDates(habit, day);
      if (windowDates.length === 0)
        continue;

      const eventStart = windowDates[0];
      if (eventStart > today)
        continue;

      const windowEnd = windowDates[windowDates.length - 1];
      const eventEnd = windowEnd;
      const key = habit.id + ':' + eventStart + ':' + eventEnd;
      if (seenWindows.has(key))
        continue;
      seenWindows.add(key);

      const value = windowValue(habit, day);
      const localDate = windowEnd > today ? today : windowEnd;
      const goal = goalForDate(habit, localDate);
      if (!goal && value === 0)
        continue;

      const met = isGoalMet(goal, value);
      events.push({
        name: `${habit.name}: ${value}`,
        start: eventStart,
        end: eventEnd,
        color: colorForGoal(goal, value),
        timed: false,
        habit,
        localDate,
        value,
        met,
      });
    }
  });

  return events;
}

const calendarEvents = computed(() => {
  const habit = selectedHabit.value;
  if (!habit)
    return [];

  return calendarEventsFor([habit]);
});

const combinedCalendarEvents = computed(() => calendarEventsFor(habitHolder.value?.habits || []));

const mobileEventsFor = (habits: Habit[], localDate: string) => {
  return habits
    .map(habit => {
      const windowDates = currentWindowDates(habit, localDate);
      if (windowDates.length === 0)
        return null;

      const value = windowValue(habit, localDate);
      const windowEnd = windowDates[windowDates.length - 1];
      const comparisonDate = windowEnd > today ? today : windowEnd;
      const goal = goalForDate(habit, comparisonDate);
      if (!goal && value === 0)
        return null;

      const future = localDate > today;
      return {
        habit,
        localDate: future ? today : localDate,
        name: future ? habit.name : `${habit.name}: ${value}`,
        value,
        color: future ? '#546E7A' : colorForGoal(goal, value),
        future,
        canEdit: !future,
      };
    })
    .filter((event): event is HabitMobileEvent => event !== null)
    .sort((left, right) => left.habit.name.localeCompare(right.habit.name));
}

const mobileDaysFor = (habits: Habit[]): HabitMobileDay[] => {
  const days = [
    { label: 'Yesterday', localDate: addDays(today, -1) },
    { label: 'Today', localDate: today },
  ];
  return days.map(day => ({
    ...day,
    events: mobileEventsFor(habits, day.localDate),
  }));
}

const selectedHabitMobileDays = computed(() => selectedHabit.value ? mobileDaysFor([selectedHabit.value]) : []);
const combinedMobileDays = computed(() => mobileDaysFor(habitHolder.value?.habits || []));

const openHabit = (habit: Habit) => {
  router.push(holderPath.value + '/' + habit.id);
}

const setSelectedHabitValueForDate = (localDate: string) => {
  const habit = selectedHabit.value;
  if (!habit || !localDate || localDate > today || valueForDate(habit, localDate) !== 0)
    return;
  increment(habit, 1, localDate);
}

const setSelectedHabitValueOnDate = (day: { date: string }) => {
  setSelectedHabitValueForDate(day?.date);
}

watch([selectedHabit, habitHolder], () => {
  resetEditHabit();
});

const createHabit = async () => {
  if (!newHabit.value.name || !newHabit.value.startDate || (newHabit.value.frequency === 'DAILY' && selectedWeekdays.value.length === 0))
    return;

  savingHabit.value = true;
  try {
    const habit = await api.rpc('habit', 'createHabit', {
      habitHolderId: props.habitHolderId,
      name: newHabit.value.name,
      frequency: newHabit.value.frequency,
      recurrenceRule: buildRecurrenceRule(),
      reminderLocalTime: newHabit.value.reminderLocalTime || null,
    });

    await api.rpc('habit', 'createHabitGoal', {
      habitHolderId: props.habitHolderId,
      habitId: habit.id,
      comparison: newHabit.value.comparison,
      goalValue: Number(newHabit.value.goalValue),
      startDate: newHabit.value.startDate,
      endDate: newHabit.value.endDate || null,
    });

    router.push(holderPath.value + '/' + habit.id);
    resetNewHabit();
  } finally {
    savingHabit.value = false;
  }
}

const saveSelectedHabit = async () => {
  const habit = selectedHabit.value;
  if (!habit || !editHabit.value.name || !editHabit.value.startDate || (editHabit.value.frequency === 'DAILY' && editSelectedWeekdays.value.length === 0))
    return;

  savingEditHabit.value = true;
  try {
    await api.rpc('habit', 'saveHabit', {
      habit: {
        ...habit,
        name: editHabit.value.name,
        frequency: editHabit.value.frequency,
        recurrenceRule: recurrenceRuleFor(editHabit.value.frequency, editSelectedWeekdays.value),
        reminderLocalTime: editHabit.value.reminderLocalTime || null,
      }
    });

    const goal = currentGoal(habit);
    if (goal) {
      await api.rpc('habit', 'saveHabitGoal', {
        habitHolderId: props.habitHolderId,
        habitGoal: {
          ...goal,
          comparison: editHabit.value.comparison,
          goalValue: Number(editHabit.value.goalValue),
          startDate: editHabit.value.startDate,
          endDate: editHabit.value.endDate || null,
        }
      });
    } else {
      await api.rpc('habit', 'createHabitGoal', {
        habitHolderId: props.habitHolderId,
        habitId: habit.id,
        comparison: editHabit.value.comparison,
        goalValue: Number(editHabit.value.goalValue),
        startDate: editHabit.value.startDate,
        endDate: editHabit.value.endDate || null,
      });
    }
  } finally {
    savingEditHabit.value = false;
  }
}

return { __sfc: true,props, emit, habitHolder, subscription, formatLocalDate, today, savingHabit, showStartDatePickerMenu, showReminderTimePickerMenu, savingEditHabit, showEditStartDatePickerMenu, showEditEndDatePickerMenu, showEditReminderTimePickerMenu, calendarFocus, frequencyOptions, rules, comparisonOptions, weekdayOptions, newHabit, selectedWeekdays, editHabit, editSelectedWeekdays, holderPath, selectedHabit, updateMenu, updateBreadcrumbs, goalsByHabitId, valuesByHabitId, goalForDate, currentGoal, valueForDate, increment, parseDate, formatDate, addDays, weekdayCode, selectedDaysForHabit, isScheduledDate, currentWindowDates, windowValue, isGoalMet, colorForGoal, resetNewHabit, recurrenceRuleFor, buildRecurrenceRule, resetEditHabit, calendarEventsFor, calendarEvents, combinedCalendarEvents, mobileEventsFor, mobileDaysFor, selectedHabitMobileDays, combinedMobileDays, openHabit, setSelectedHabitValueForDate, setSelectedHabitValueOnDate, createHabit, saveSelectedHabit, api, Card, EditableObject, Permissions }
}

})